/*
 * Native variant picker: style image cards + priced size grid (replacing the Swatch King app).
 * Markup: snippets/variant-button.liquid (cards, size tiles) and snippets/product-form.liquid
 * (the add button's size-gate markers). Liquid paints the first frame; this keeps it current.
 *
 * THEME UPDATE NOTE:
 * Must load AFTER theme.js (needs theme.Currency, and its Variants change handler has to be
 * bound before a shopper can click anything we re-paint):
 *   <script src="{{ 'variant-cards.js' | asset_url }}" defer="defer"></script>
 *
 * The theme's radios stay the single source of truth, exactly as they are for sticky-atc.js.
 * Nothing here sets .checked, replaces an input, or re-parents one: theme.js reads the changed
 * option from `srcElement.parentElement.dataset.index`, so every input's direct parent must
 * stay its .variant-input div. Tiles are only hidden/disabled, re-priced and re-ordered.
 *
 * Three jobs:
 *   1. refresh() — size upcharges, hide what can't be bought (never struck through, never the
 *      checked value), admin size order, the style header, the checked style's fit note.
 *   2. The size gate — the theme pre-checks a size the shopper never chose, so until they pick
 *      one the add button reads its data-gate-text ("Select a size") and a trusted click on it
 *      (or on Buy it now) is swallowed: focus goes to the size grid, the grid is scrolled into
 *      view and flagged (`is-attention`), and a visible role="status" prompt above it shows
 *      the button's data-gate-prompt ("Please select a size"). aria-disabled, never the real
 *      `disabled`: that stays the theme's sold-out state, and a gate that can't be lifted by
 *      JS must never be able to strand the button. The GATE fails open: no JS, no visible size
 *      grid or a disabled button means no gate. (The Liquid first-paint `hidden`/`disabled` on
 *      sold-out tiles is a different matter: only this script undoes it, via refresh() or
 *      standDown().)
 *   3. The "Size chart" button beside the size heading opens and scrolls to the sizing tab.
 *
 * SWATCH KING: if the app's <variant-swatch-king> is in the section (now or later — it is
 * JS-inserted after DOMContentLoaded), this script stands down for that section: it undoes
 * every hidden/disabled it or Liquid applied, lifts the gate, and does nothing more. How the
 * app drives the variant is UNVERIFIED — it may click the theme radios (which a disabled radio
 * would swallow) or set the variant some other way. Re-enabling every radio makes either safe.
 * It also shows its own "Select a size" prompt. See standDown().
 */

(function () {
  'use strict';

  var ROOT_SELECTOR = '[id^="ProductSection-"]';
  var PICKER_SELECTOR = '.variant-input-wrap--sizes, .variant-input-wrap--cards';
  var ADD_SELECTOR = 'button.add-to-cart[data-add-to-cart]';
  var SWATCH_KING_SELECTOR = 'variant-swatch-king';
  // Ownership marker: set on the button only while THIS script has written the gate text and
  // aria-disabled, so lifting the gate never has to guess by comparing text.
  var APPLIED_ATTR = 'data-size-gate-applied';
  // First-paint marker on the add button: the gate has been decided (applied or not) at least
  // once. variant-cards.css keeps a gated button in its not-ready colour until this exists, so
  // it never flashes "ready" before the gate lands.
  var DECIDED_ATTR = 'data-gate-decided';
  // Restarts the CSS attention animation on the size fieldset (see attention()).
  var ATTENTION_CLASS = 'is-attention';
  // The size gate's prompt, rendered empty by snippets/variant-button.liquid before the size
  // fieldset (only when there is more than one size).
  var PROMPT_SELECTOR = '.variant-size__prompt[role="status"]';

  // One entry per product section:
  //   { root, variants, chosenSize, stoodDown, swatchMO, describedRadios }
  // describedRadios: the radios showPrompt() added the prompt id to, so clearPrompt() can take
  // it off again.
  // Pruned of detached roots on every init (their observer disconnected), so a section reload
  // in the editor doesn't leave the old root behind.
  var entries = [];

  function money(cents) {
    return theme.Currency.formatMoney(cents, theme.settings.moneyFormat);
  }

  // Mirrors Liquid's `money_without_trailing_zeros`, which the size tiles' first paint uses:
  // a zero-decimal tail is dropped, a real one kept. "$2.00" → "$2", "$1.50" stays,
  // "2,00 €" → "2 €". The lookahead skips a thousands group ("$1,000.00" → "$1,000").
  function priceText(cents) {
    return money(cents).replace(/[.,]00(?!\d)/, '');
  }

  // ====== DATA ======
  //
  // ponytail: duplicated from sticky-atc.js (variantMatches / expectedFrom / the findVariant
  // loop, ~20 lines) rather than extracted, because sticky is a private IIFE and two consumers
  // don't earn a shared module. Extract if a third consumer appears. The rationale for
  // all-options matching lives in sticky-atc.js's docblocks; the semantics here are identical.

  // Only the optionN keys present in `expected` are compared, so a 2-option product simply
  // never tests option3.
  function variantMatches(variant, expected) {
    for (var index in expected) {
      if (!Object.prototype.hasOwnProperty.call(expected, index)) continue;
      if (variant[index] !== expected[index]) return false;
    }
    return true;
  }

  // The current selection minus up to two axes. An absent axis arrives as `undefined` (or
  // null), which no optionN key equals, so an unused skip argument excludes nothing.
  function expectedFrom(base, skipA, skipB) {
    var expected = {};
    for (var index in base) {
      if (Object.prototype.hasOwnProperty.call(base, index) && index !== skipA && index !== skipB) {
        expected[index] = base[index];
      }
    }
    return expected;
  }

  function findVariant(variants, expected) {
    for (var i = 0; i < variants.length; i++) {
      if (variantMatches(variants[i], expected)) return variants[i];
    }
    return null;
  }

  // ====== DOM ======

  function sizeWrap(root) {
    return root.querySelector('.variant-input-wrap--sizes');
  }

  function styleWrap(root) {
    return root.querySelector('.variant-input-wrap--cards');
  }

  function tilesOf(wrap) {
    return [].slice.call(wrap.querySelectorAll('.variant-input'));
  }

  function inputOf(tile) {
    return tile.querySelector('input[data-variant-input]');
  }

  function checkedInput(wrap) {
    return wrap.querySelector('input[data-variant-input]:checked');
  }

  // Every option currently selected in this section, e.g. {option1: 'Tee - Black', option2: 'M'}.
  // Mirrors sticky-atc.js's selectedOptions(); a select (variant-dropdown.liquid) counts too.
  function selectedOptions(root) {
    var selected = {};
    root.querySelectorAll('.variant-input-wrap[data-index]').forEach(function (wrap) {
      var inputs = wrap.querySelectorAll('[data-variant-input]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].tagName === 'SELECT' || inputs[i].checked) {
          selected[wrap.getAttribute('data-index')] = inputs[i].value;
          return;
        }
      }
    });
    return selected;
  }

  // Hidden + disabled together: `hidden` takes the tile out of sight, `disabled` takes the radio
  // out of the arrow-key ring and the form. The CHECKED input is never either, whatever the data
  // says — that is the case where theme.js's _getClosestAvailableMatch has just checked a size
  // our last pass (or Liquid) had hidden, and a hidden checked radio would leave the shopper
  // with a selection they can't see.
  // Keep these hiding rules aligned with sticky-atc.js: its setOption() selects via
  // input.click(), which a disabled radio silently swallows.
  function setOffered(tile, input, offered) {
    if (input && input.checked) offered = true;
    if (tile.hidden === !offered && (!input || input.disabled === !offered)) return;
    tile.hidden = !offered;
    if (input) input.disabled = !offered;
  }

  // Run a mutation that can blur focus inside `wrap` (appendChild re-parenting blurs, and a
  // display:none tile loses focus on the next style recalc), then put focus back: on the same
  // control when it is still offered, otherwise on the checked radio. preventScroll because
  // focus was already there — this is a repair, not a navigation.
  function keepFocus(wrap, mutate) {
    var focused = wrap.contains(document.activeElement) ? document.activeElement : null;
    mutate();
    if (!focused) return;
    var tile = focused.closest('.variant-input');
    var target = tile && !tile.hidden ? focused : checkedInput(wrap);
    if (target && document.activeElement !== target) target.focus({ preventScroll: true });
  }

  // ====== REFRESH ======

  // SIZE BASE PRICE — mirrors the rule above the value loop in snippets/variant-button.liquid
  // exactly; change both or neither. Candidates: every variant matching the current selection
  // at every position except size. Base = the lowest price among the AVAILABLE candidates; if
  // none is available, the lowest among all candidates; 0 if there are none. Recomputed on
  // every refresh, because a style change changes it.
  function sizeBasePrice(variants, rest) {
    var any = null;
    var available = null;
    variants.forEach(function (variant) {
      if (!variantMatches(variant, rest)) return;
      if (any === null || variant.price < any) any = variant.price;
      if (variant.available && (available === null || variant.price < available)) available = variant.price;
    });
    if (available !== null) return available;
    return any === null ? 0 : any;
  }

  // A tile's price span: only the upcharge over the base, otherwise EMPTY (no separator
  // either). Byte-for-byte the Liquid fragment, so accessible names read "4XL, +$2 more" and
  // just "M".
  function upchargeHtml(match, base) {
    if (!match || !(match.price > base)) return '';
    return '<span class="visually-hidden">, </span>+' + priceText(match.price - base) +
      '<span class="visually-hidden"> more</span>';
  }

  function refreshSizes(entry, selected) {
    var wrap = sizeWrap(entry.root);
    if (!wrap) return;
    var index = wrap.getAttribute('data-index');
    var tiles = tilesOf(wrap);
    if (!tiles.length) return;
    var rest = expectedFrom(selected, index);
    var base = sizeBasePrice(entry.variants, rest);

    keepFocus(wrap, function () {
      tiles.forEach(function (tile) {
        var input = inputOf(tile);
        if (!input) return;
        // The variant this tile would select: every current selection, this size substituted.
        var expected = expectedFrom(selected);
        expected[index] = input.value;
        var match = findVariant(entry.variants, expected);

        var priceEl = tile.querySelector('[data-size-price]');
        var price = upchargeHtml(match, base);
        if (priceEl && priceEl.innerHTML !== price) priceEl.innerHTML = price;

        setOffered(tile, input, !!match && match.available);
      });

      // Admin order. DO NOT "simplify" back to Liquid / option.values order: that is
      // first-occurrence order across the style-major variant array, so a size only a later
      // style carries (XS, on Crop Baby Tee) lands last. The real order is the size
      // subsequence of the variants matching every selection EXCEPT size — see
      // orderedSizeButtons() in sticky-atc.js for the full story. DOM order, not CSS `order`,
      // because radio arrow keys and screen readers follow the DOM.
      var order = [];
      entry.variants.forEach(function (variant) {
        if (variantMatches(variant, rest) && order.indexOf(variant[index]) === -1) order.push(variant[index]);
      });
      var ordered = [];
      order.forEach(function (value) {
        tiles.forEach(function (tile) {
          var input = inputOf(tile);
          if (input && input.value === value) ordered.push(tile);
        });
      });
      // Sizes with no variant under the current selection keep their relative order as a tail.
      tiles.forEach(function (tile) {
        if (ordered.indexOf(tile) === -1) ordered.push(tile);
      });

      // Only when the sequence actually changed: re-appending blurs, and a size pick never
      // changes the sequence (it is derived from everything except size).
      var moved = ordered.some(function (tile, position) { return tiles[position] !== tile; });
      if (moved) {
        var parent = tiles[0].parentNode;
        ordered.forEach(function (tile) { parent.appendChild(tile); });
      }
    });
  }

  // A style is offered when SOME available variant of it matches every other selection,
  // ignoring the size axis — whatever size happens to be checked must not hide a style. Same
  // rule as sticky-atc.js's styleAvailable() and the Liquid first paint.
  function refreshStyles(entry, selected) {
    var wrap = styleWrap(entry.root);
    if (!wrap) return;
    var index = wrap.getAttribute('data-index');
    var sizes = sizeWrap(entry.root);
    var sizeIndex = sizes ? sizes.getAttribute('data-index') : null;
    var rest = expectedFrom(selected, index, sizeIndex);

    keepFocus(wrap, function () {
      tilesOf(wrap).forEach(function (tile) {
        var input = inputOf(tile);
        if (!input) return;
        var offered = entry.variants.some(function (variant) {
          return variant[index] === input.value && variant.available && variantMatches(variant, rest);
        });
        setOffered(tile, input, offered);
      });
    });

    var label = entry.root.querySelector('[data-option-value-label]');
    if (label && selected[index] !== undefined && label.textContent !== selected[index]) {
      label.textContent = selected[index];
    }

    refreshFitNote(entry, wrap);
  }

  // The checked style's fit note, copied from its .variant-input's data-fit-note (Liquid
  // renders every style's note there; snippets/style-fit-note.liquid is the copy). textContent
  // only — it is merchant copy, never markup. An empty note is `hidden` AND emptied: the style
  // fieldset's aria-describedby points here, and aria-describedby reads hidden targets.
  function refreshFitNote(entry, wrap) {
    var display = entry.root.querySelector('[data-fit-note-display]');
    if (!display) return;
    var checked = checkedInput(wrap);
    var tile = checked && checked.closest('.variant-input');
    var note = (tile && tile.getAttribute('data-fit-note')) || '';
    if (display.textContent !== note) display.textContent = note;
    if (display.hidden !== !note) display.hidden = !note;
  }

  // ====== SIZE GATE ======

  // The ONE gate predicate. applyGate() paints from it and the click guard swallows clicks from
  // it, so the two can never disagree — a click is never eaten while no gate is showing.
  // Recomputed from the live DOM every time; the click guard must never trust cached state.
  // Returns the elements the gate needs when it is active, null otherwise. Every clause fails
  // OPEN:
  //   - stood down: Swatch King owns this section's picker (see standDown())
  //   - no button / grid / text span / gate copy: nothing to show, so nothing to enforce
  //   - no data-size-gate: Liquid decided there is nothing to choose (one size, gift card,
  //     preorder)
  //   - button `disabled`: theme.js's sold-out / unavailable state owns the button
  //   - size grid not rendered: a gate the shopper has no visible control to lift would
  //     strand the button
  // Then the gate is "the checked size is not the size the shopper chose". Values, not a
  // boolean latch: when a style change makes theme.js auto-switch the size, the shopper didn't
  // choose the new one, so it has to re-gate. Same rationale as sticky-atc.js's chosenSize.
  function activeGate(entry) {
    if (entry.stoodDown) return null;
    var btn = entry.root.querySelector(ADD_SELECTOR);
    var wrap = sizeWrap(entry.root);
    var textEl = btn && btn.querySelector('[data-add-to-cart-text]');
    var gateText = btn && btn.getAttribute('data-gate-text');
    if (!btn || !wrap || !textEl || !gateText) return null;
    if (!btn.hasAttribute('data-size-gate') || btn.disabled) return null;
    if (!wrap.getClientRects().length) return null;
    var checked = checkedInput(wrap);
    if ((checked ? checked.value : null) === entry.chosenSize) return null;
    return { btn: btn, wrap: wrap, textEl: textEl, gateText: gateText };
  }

  // Lift the gate — but only what WE applied. The ownership attribute decides, never the text:
  // the button's copy is shared with theme.js (sold out, unavailable, default) and with
  // anything else that writes to it. The text is restored only while the button is enabled;
  // a disabled button is showing theme.js's sold-out/unavailable copy, which wins.
  // The prompt and the unconfirmed marker are ours alone, so they are cleared
  // unconditionally, before the ownership check.
  function clearGate(entry) {
    var wrap = sizeWrap(entry.root);
    if (wrap) wrap.removeAttribute('data-size-unconfirmed');
    describeUnconfirmed(entry, wrap, null);
    clearPrompt(entry);
    var btn = entry.root.querySelector(ADD_SELECTOR);
    if (!btn || !btn.hasAttribute(APPLIED_ATTR)) return;
    btn.removeAttribute('aria-disabled');
    var textEl = btn.querySelector('[data-add-to-cart-text]');
    var defaultText = textEl && textEl.getAttribute('data-default-text');
    if (textEl && defaultText && !btn.disabled) textEl.textContent = defaultText;
    btn.removeAttribute(APPLIED_ATTR);
  }

  // variant:change fires AFTER the container's variantChange, i.e. after theme.js's
  // updateCartButton has reset the text from data-default-text — so re-applying here wins.
  function applyGate(entry) {
    var gate = activeGate(entry);
    if (!gate) {
      clearGate(entry);
      return;
    }
    gate.wrap.setAttribute('data-size-unconfirmed', '');
    describeUnconfirmed(entry, gate.wrap, checkedInput(gate.wrap));
    gate.btn.setAttribute('aria-disabled', 'true');
    gate.btn.setAttribute(APPLIED_ATTR, '');
    if (gate.textEl.textContent !== gate.gateText) gate.textEl.textContent = gate.gateText;
  }

  // Release every add button inside `container` for its "ready" colour: the gate has been
  // decided at least once — applied OR not, so a button with no gate turns ready too. init()
  // calls it per picker root after that root's first refresh, then once over the whole scope,
  // so a root without the native picker (a dropdown picker, a featured product, or variant
  // JSON that wouldn't parse) is released as well: no entry means no gate, ever.
  // Selects `[data-size-gate]` as well as ADD_SELECTOR: variant-cards.css holds ANY gated
  // `.add-to-cart[data-size-gate]` dark until this marker exists, so the release has to cover
  // at least everything that hold rule can match, or a button outside ADD_SELECTOR's shape
  // would stay dark for good.
  function markDecided(container) {
    container.querySelectorAll(ADD_SELECTOR + ', [data-size-gate]').forEach(function (btn) {
      if (!btn.hasAttribute(DECIDED_ATTR)) btn.setAttribute(DECIDED_ATTR, '');
    });
  }

  // ====== PROMPT ======

  // A swallowed click explains itself through ONE visible <p role="status"> above the size
  // fieldset, rendered empty by snippets/variant-button.liquid (a live region present from page
  // load is announced more reliably than one inserted later). Empty, it takes no space:
  // variant-cards.css collapses it, and the one-line shift when the text appears on a blocked
  // tap is accepted, because revealSizes() measures after the text is written. It lives
  // beside the size fieldset, NEVER inside the button: inside, its text would join the
  // button's accessible name. Absent (one size, a theme update that dropped it): no prompt,
  // but a blocked click still focuses and scrolls to the grid.
  function promptOf(entry) {
    return entry.root.querySelector(PROMPT_SELECTOR);
  }

  // aria-describedby is a token list, and theme.js's describeSoldOut() writes the sold-out
  // note's id to these same radios. So the prompt id is added and removed as ONE token, never
  // by overwriting or removing the whole attribute. That protection is one-directional: it
  // keeps theme.js's id safe from us, but describeSoldOut() itself still setAttribute()s or
  // removeAttribute()s the WHOLE attribute, so a style change while the prompt is showing can
  // drop our token from the radio. The fieldset's token and the visible prompt text remain.
  function addDescribedBy(el, id) {
    var tokens = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (tokens.indexOf(id) !== -1) return;
    tokens.push(id);
    el.setAttribute('aria-describedby', tokens.join(' '));
  }

  function removeDescribedBy(el, id) {
    var tokens = (el.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
    if (tokens.indexOf(id) === -1) return;
    tokens = tokens.filter(function (token) { return token !== id; });
    if (tokens.length) el.setAttribute('aria-describedby', tokens.join(' '));
    else el.removeAttribute('aria-describedby');
  }

  // The pre-ticked size is `checked` but drawn unselected while the gate is up, so a screen
  // reader would announce "S, checked" against a button saying "Select a size". The hidden
  // "No size chosen yet." note (variant-button.liquid, beside the prompt) is added as a
  // describedby TOKEN to `checked` only, and taken off every other size radio, since theme.js
  // can auto-switch which radio is checked. Radio only, not the fieldset: the radio is where
  // "checked" contradicts the screen, and a fieldset description would be read again on
  // entering the group, saying it twice. `checked` null removes it everywhere (clearGate).
  // Token-based for the same reason as the prompt: theme.js's describeSoldOut() shares these
  // radios' aria-describedby. It runs on the container's variantChange, BEFORE our
  // variant:change applyGate(), so a token it wipes is re-added.
  function describeUnconfirmed(entry, wrap, checked) {
    var note = entry.root.querySelector('[data-size-unconfirmed-note]');
    if (!note || !wrap) return;
    wrap.querySelectorAll('input[data-variant-input]').forEach(function (radio) {
      if (radio === checked) addDescribedBy(radio, note.id);
      else removeDescribedBy(radio, note.id);
    });
  }

  // Undo everything showPrompt() did: the text, our describedby token on every radio we gave
  // it to and on the fieldset, and the attention flag.
  function clearPrompt(entry) {
    var el = promptOf(entry);
    if (el && el.textContent) el.textContent = '';
    var radios = entry.describedRadios || [];
    entry.describedRadios = [];
    var wrap = sizeWrap(entry.root);
    if (el) {
      radios.forEach(function (radio) { removeDescribedBy(radio, el.id); });
      if (wrap) removeDescribedBy(wrap, el.id);
    }
    if (wrap) wrap.classList.remove(ATTENTION_CLASS);
  }

  // Restart the attention animation: removing and re-adding the class in one frame is a no-op
  // to the style engine, so a forced reflow between them makes the second tap animate too.
  function attention(wrap) {
    if (wrap.classList.contains(ATTENTION_CLASS)) {
      wrap.classList.remove(ATTENTION_CLASS);
      void wrap.offsetWidth;
    }
    wrap.classList.add(ATTENTION_CLASS);
  }

  // Called BEFORE focus moves to `target`. Two deliveries, deliberately:
  //   - the prompt id goes on the target radio's (and the fieldset's) aria-describedby, so the
  //     prompt is part of the focus announcement itself. VoiceOver on iOS tends to drop polite
  //     live text that arrives while it is speaking a focus change;
  //   - role="status" carries it when focus does NOT move (a repeat tap, focus already on the
  //     radio). A repeat write alternates a trailing U+00A0, so the text always changes and is
  //     re-announced, with no blank-then-rewrite timer.
  // DEVICE-QA ITEM: NVDA may read the prompt twice on the first tap (description + status).
  function showPrompt(entry, gate, target) {
    attention(gate.wrap);
    var el = promptOf(entry);
    if (!el) return;
    var text = gate.btn.getAttribute('data-gate-prompt') || gate.gateText;
    el.textContent = el.textContent === text ? text + '\u00A0' : text;
    addDescribedBy(gate.wrap, el.id);
    if (target) {
      addDescribedBy(target, el.id);
      if (entry.describedRadios.indexOf(target) === -1) entry.describedRadios.push(target);
    }
  }

  // Bring the prompt and the size grid on screen if any of them is off screen. Measured from
  // the prompt's top (after its text is written, so its height is real) to the grid's bottom,
  // so the prompt isn't left under the sticky header. The theme's scroll-padding (sticky header
  // on top, the sticky ATC bar at the bottom) is what scrollIntoView honours, so the same
  // padding defines "on screen" here.
  function revealSizes(entry, wrap) {
    var rect = wrap.getBoundingClientRect();
    var prompt = promptOf(entry);
    var top = prompt ? Math.min(prompt.getBoundingClientRect().top, rect.top) : rect.top;
    var style = getComputedStyle(document.documentElement);
    var padTop = parseFloat(style.scrollPaddingTop) || 0;
    var padBottom = parseFloat(style.scrollPaddingBottom) || 0;
    if (top >= padTop && rect.bottom <= window.innerHeight - padBottom) return;
    wrap.scrollIntoView({ block: 'center', behavior: scrollBehavior() });
  }

  function scrollBehavior() {
    var reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    return reduced ? 'auto' : 'smooth';
  }

  // ====== SWATCH KING ======

  // Hand the section back to the theme's stock behaviour. Every input in both fieldsets is
  // re-enabled (this also undoes the Liquid first-paint `disabled`), every tile un-hidden, the
  // gate lifted (clearGate() also empties the prompt), and
  // the button released for its ready colour. After this refresh() and the click guard are
  // no-ops for the entry — the app owns the picker and its own "Select a size" prompt.
  function standDown(entry) {
    entry.stoodDown = true;
    if (entry.swatchMO) {
      entry.swatchMO.disconnect();
      entry.swatchMO = null;
    }
    [sizeWrap(entry.root), styleWrap(entry.root)].forEach(function (wrap) {
      if (!wrap) return;
      wrap.querySelectorAll('input[data-variant-input]').forEach(function (input) { input.disabled = false; });
      tilesOf(wrap).forEach(function (tile) { tile.hidden = false; });
    });
    clearGate(entry);
    markDecided(entry.root);
  }

  function refresh(entry) {
    if (entry.stoodDown) return;
    var selected = selectedOptions(entry.root);
    refreshSizes(entry, selected);
    refreshStyles(entry, selected);
    applyGate(entry);
  }

  function refreshAll() {
    entries.forEach(refresh);
  }

  // Where to send a shopper whose add was swallowed: the checked size, else the first offered
  // one. The radios are 1px-clipped, not display:none, so focusing one rings its label.
  function sizeTarget(wrap) {
    var target = checkedInput(wrap);
    if (target) return target;
    var offered = tilesOf(wrap).filter(function (tile) { return !tile.hidden; })[0];
    return (offered && inputOf(offered)) || null;
  }

  // ====== INIT ======

  function entryFor(el) {
    var root = el.closest(ROOT_SELECTOR);
    if (!root) return null;
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].root === root) return entries[i];
    }
    return null;
  }

  function initRoot(root) {
    for (var i = 0; i < entries.length; i++) {
      if (entries[i].root === root) return entries[i];
    }
    var jsonEl = root.querySelector('[data-variant-json]');
    if (!jsonEl) return null;
    var variants;
    try {
      variants = JSON.parse(jsonEl.value || jsonEl.textContent);
    } catch (e) {
      return null;
    }
    if (!Array.isArray(variants) || !variants.length) return null;
    var entry = {
      root: root, variants: variants, chosenSize: null,
      stoodDown: false, swatchMO: null, describedRadios: []
    };
    entries.push(entry);

    // Swatch King already here: stand down before painting anything. Otherwise watch for it —
    // it is NOT in the server HTML, the app's JS inserts it after DOMContentLoaded. Same
    // pattern as sticky-atc.js's swatchMO: one observer, disconnected the moment the element
    // lands (standDown() does that). With the app absent it stays connected until the entry is
    // pruned. Our own size re-ordering also triggers it; the callback is one querySelector.
    if (root.querySelector(SWATCH_KING_SELECTOR)) {
      standDown(entry);
    } else {
      entry.swatchMO = new MutationObserver(function () {
        if (root.querySelector(SWATCH_KING_SELECTOR)) standDown(entry);
      });
      entry.swatchMO.observe(root, { childList: true, subtree: true });
    }
    return entry;
  }

  // Featured-product sections render the same snippet, so one entry per section root and every
  // lookup scoped to it. markDecided() runs per picker root after its first refresh() has
  // decided the gate, then over every add button in the scope (fail open: a root with no entry
  // — dropdown picker, no native grid, unparseable JSON — will never be gated).
  // Both releases sit in `finally`. variant-cards.css holds a gated button dark until it is
  // marked decided, so an exception anywhere in initRoot()/refresh() (a theme.Currency that
  // throws, unexpected markup) would otherwise leave the add button dark for good. The error
  // still propagates to the console: `finally` releases, it doesn't swallow.
  function init(scope) {
    var container = scope || document;
    try {
      entries = entries.filter(function (entry) {
        if (entry.root.isConnected) return true;
        if (entry.swatchMO) entry.swatchMO.disconnect();
        return false;
      });
      container.querySelectorAll(PICKER_SELECTOR).forEach(function (wrap) {
        var root = wrap.closest(ROOT_SELECTOR);
        if (!root) return;
        try {
          var entry = initRoot(root);
          if (entry) refresh(entry);
        } finally {
          markDecided(root);
        }
      });
    } finally {
      markDecided(container);
    }
  }

  // ====== LISTENERS (bound once; they iterate the entries) ======

  var SIZE_INPUT_SELECTOR = '.variant-input-wrap--sizes input[data-variant-input]';

  // Record a deliberate size choice and refresh. Tapping the PRE-checked size fires no change
  // and no variant:change, so refresh directly; deferred so theme.js's own change handling (and
  // our variant:change refresh) finishes first. Idempotent.
  function confirmSize(input) {
    var entry = entryFor(input);
    if (!entry || entry.stoodDown) return;
    entry.chosenSize = input.value;
    // The prompt has been answered: empty it, drop our describedby tokens (radios and
    // fieldset) and the attention flag.
    clearPrompt(entry);
    setTimeout(function () { refresh(entry); }, 0);
  }

  // A click on a size radio is the shopper's deliberate choice — trusted or not: sticky-atc.js
  // drives these radios with input.click(), keyboard arrow selection dispatches click, and
  // screen-reader activation (NVDA/JAWS browse mode, VoiceOver double-tap) does too.
  document.addEventListener('click', function (evt) {
    var input = evt.target.closest && evt.target.closest(SIZE_INPUT_SELECTOR);
    if (input) confirmSize(input);
  });

  // Space on the ALREADY-checked size is the keyboard's way to confirm the pre-ticked size, but
  // Blink and WebKit dispatch no click for it (the radio is already checked), so the click
  // listener above never hears it and the gate would stay up. A blocked add sends focus to
  // exactly that radio, so without this a keyboard user's only way out was arrowing away and
  // back. Space on an UNchecked radio does click; confirmSize() is idempotent, so hearing it
  // here as well is harmless.
  document.addEventListener('keyup', function (evt) {
    if (evt.key !== ' ' && evt.key !== 'Spacebar') return;
    var input = evt.target.closest && evt.target.closest(SIZE_INPUT_SELECTOR);
    if (input && input.checked) confirmSize(input);
  });

  // "Size chart" (snippets/variant-button.liquid's [data-size-guide], beside the size heading):
  // open the sizing tab it names and bring it on screen. Works in any root and needs no entry —
  // it is a plain link to on-page content. Looked up in the link's own section first (the tab
  // id is block id + product id, so the same product rendered twice on a page — main product
  // plus a featured-product section — duplicates it), then the whole document. Opening goes
  // through the trigger's own click, so the theme's Collapsibles does the opening and assets/collapsible-image-defer.js hydrates the
  // chart images from its capture-phase listener; never toggle is-open or the height here.
  // Only clicked while closed, so a second tap never closes the chart it just opened.
  document.addEventListener('click', function (evt) {
    var link = evt.target.closest && evt.target.closest('[data-size-guide]');
    if (!link) return;
    var id = link.getAttribute('data-size-guide');
    if (!id) return;
    var escaped = window.CSS && CSS.escape ? CSS.escape(id) : id;
    var selector = '.collapsible-trigger[aria-controls="' + escaped + '"]';
    var root = link.closest(ROOT_SELECTOR);
    var trigger = (root && root.querySelector(selector)) || document.querySelector(selector);
    if (!trigger) return;
    if (!trigger.classList.contains('is-open')) trigger.click();
    trigger.scrollIntoView({ block: 'start', behavior: scrollBehavior() });
    trigger.focus({ preventScroll: true });
  });

  // The click guard. WINDOW capture so it runs before anything else on the page — in particular
  // before the Monster Upsells app's document-level click hijack, which would otherwise add the
  // pre-checked size before we got a say. Only TRUSTED clicks are judged: sticky-atc.js adds by
  // calling inlineBtn.click() and runs its own size gate, so its clicks always pass.
  window.addEventListener('click', function (evt) {
    if (!evt.isTrusted || !evt.target.closest) return;
    var hit = evt.target.closest('[data-size-gate], .shopify-payment-button');
    if (!hit) return;
    var entry = entryFor(hit);
    if (!entry || entry.stoodDown) return;

    var gate = activeGate(entry);
    if (gate) {
      evt.preventDefault();
      evt.stopImmediatePropagation();
      refresh(entry);
      // Prompt FIRST, then focus: the prompt id is on the target radio's aria-describedby by
      // the time focus lands, so it is read as part of the focus announcement (see
      // showPrompt()). preventScroll, because focus() would scroll the 1px radio to the nearest
      // edge; revealSizes() then centres the grid, measured with the prompt's text in place.
      var target = sizeTarget(gate.wrap);
      showPrompt(entry, gate, target);
      if (target) target.focus({ preventScroll: true });
      revealSizes(entry, gate.wrap);
      return;
    }
    // Let it through, but clear any gate text a missed event left behind.
    refresh(entry);
  }, true);

  // Foreign pickers (quick-shop, featured-product) dispatch the same document-level event, and
  // on size-only products an option-value match would collide across products, so the root is
  // found by variant id. A null variant (an unresolvable combination) can't be attributed:
  // refresh everything, it's cheap.
  document.addEventListener('variant:change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    if (!variant) {
      refreshAll();
      return;
    }
    entries.forEach(function (entry) {
      var ours = entry.variants.some(function (candidate) { return candidate.id === variant.id; });
      if (ours) refresh(entry);
    });
  });

  document.addEventListener('shopify:section:load', function (evt) {
    init(evt.target);
  });

  // Belt and braces alongside swatchMO: anything else that hides the theme picker after
  // DOMContentLoaded is caught by the gate's visibility clause once everything has loaded.
  window.addEventListener('load', refreshAll);

  if (document.readyState === 'loading') {
    // Wrapped: init() takes a scope, and the DOMContentLoaded event object isn't one.
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
